import { useState } from "react";

/* ─────────────────────────────────────────
   CYBER TRACK PAGE  — مسار الأمن السيبراني
   Brand accent: Shield #1A9070
   ───────────────────────────────────────── */

const SHIELD = "#1A9070";
const SHIELD2 = "#22B58A";
const MINT = "#6DD3A7";
const GOLD = "#F2B949";

/* ── small shared bits ── */
const Tag = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-[.68rem] font-medium bg-[rgba(26,144,112,.08)] border border-[rgba(26,144,112,.18)] text-[#22B58A] px-[9px] py-[3px] rounded-full whitespace-nowrap">
    {children}
  </span>
);

const Check = ({ children }) => (
  <li className="flex items-start gap-3 text-[.88rem] text-[#94B4C8] leading-[1.7]">
    <span className="mt-[3px] w-[18px] h-[18px] rounded-full border border-[rgba(26,144,112,.4)] flex items-center justify-center flex-shrink-0">
      <svg className="w-[9px] h-[9px]" viewBox="0 0 10 8" fill="none">
        <polyline points="1 4 3.5 6.5 9 1" stroke="#22B58A" strokeWidth="1.8" />
      </svg>
    </span>
    <span>{children}</span>
  </li>
);

const StatBox = ({ n, label }) => (
  <div className="text-center">
    <span className="block font-cairo font-black text-[1.8rem] leading-none text-[#22B58A] mb-[3px]">
      {n}
    </span>
    <span className="block text-[.62rem] text-[#3D6278] tracking-[.08em] uppercase">
      {label}
    </span>
  </div>
);

/* ── Module card (accordion) ── */
function Module({ num, title, tools, outcomes, href, open, onToggle }) {
  return (
    <div
      className={`rounded-[12px] border overflow-hidden transition-all duration-300 ${
        open
          ? "border-[rgba(26,144,112,.3)] bg-[#0C1F35]"
          : "border-[rgba(109,211,167,.09)] bg-[#091828]"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-[clamp(16px,2vw,24px)] py-[clamp(14px,1.8vh,20px)] text-right"
      >
        <span
          className="flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center font-mono text-[.58rem] font-bold"
          style={{
            background: open ? SHIELD : "rgba(26,144,112,.12)",
            color: open ? "#fff" : SHIELD2,
          }}
        >
          {String(num).padStart(2, "0")}
        </span>
        <span className="flex-1 font-cairo font-bold text-[clamp(.9rem,1.1vw,1.05rem)] text-[#F7F2E9] text-right leading-[1.3]">
          {title}
        </span>
        <svg
          className={`w-4 h-4 text-[#3D6278] transition-transform duration-300 flex-shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="px-[clamp(16px,2vw,24px)] pb-[clamp(16px,2vh,22px)]">
          <div className="h-px bg-[rgba(26,144,112,.15)] mb-[16px]" />
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <p className="font-mono text-[.5rem] tracking-[.14em] uppercase text-[#3D6278] mb-[8px]">
                الأدوات والمفاهيم
              </p>
              <div className="flex flex-wrap gap-1.5">
                {tools.map((t) => (
                  <span
                    key={t}
                    className="text-[.66rem] bg-[rgba(26,144,112,.06)] border border-[rgba(26,144,112,.14)] text-[#22B58A] px-[8px] py-[2px] rounded-full"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="font-mono text-[.5rem] tracking-[.14em] uppercase text-[#3D6278] mb-[8px]">
                ماذا ستنجز
              </p>
              <ul className="space-y-[6px]">
                {outcomes.map((o) => (
                  <li
                    key={o}
                    className="flex items-start gap-2 text-[.76rem] text-[#94B4C8] leading-[1.6]"
                  >
                    <span className="text-[#22B58A] mt-[2px] flex-shrink-0">
                      ◆
                    </span>
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener"
              className="mt-[16px] inline-flex items-center gap-2 font-cairo font-bold text-[.82rem] text-[#22B58A] bg-[rgba(26,144,112,.07)] border border-[rgba(26,144,112,.18)] px-[16px] py-[8px] rounded-[4px] transition-all duration-200 hover:bg-[#1A9070] hover:text-white hover:border-[#1A9070]"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              سجّل في هذه المحطة
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ── FAQ ── */
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`rounded-[10px] border overflow-hidden transition-all duration-200 ${open ? "border-[rgba(26,144,112,.25)] bg-[#0C1F35]" : "border-[rgba(109,211,167,.09)] bg-[#091828]"}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-[20px] py-[16px] text-right"
      >
        <span className="font-cairo font-bold text-[clamp(.86rem,.95rem,1rem)] text-[#F7F2E9] leading-[1.3]">
          {q}
        </span>
        <span
          className={`w-[26px] h-[26px] rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-200 ${open ? "border-[#1A9070] bg-[rgba(26,144,112,.1)]" : "border-[rgba(109,211,167,.2)]"}`}
        >
          <svg
            className={`w-[11px] h-[11px] stroke-[#22B58A] transition-transform duration-300 ${open ? "rotate-45" : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            strokeWidth="2.5"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-[20px] pb-[16px] border-t border-[rgba(26,144,112,.08)]">
          <p
            className="pt-[12px] text-[.85rem] text-[#94B4C8] leading-[1.85]"
            dangerouslySetInnerHTML={{ __html: a }}
          />
        </div>
      )}
    </div>
  );
}

/* ══ PAGE DATA ══════════════════════════════ */

const MODULES = [
  {
    num: 1,
    title: "أساسيات الأمن السيبراني والهندسة الاجتماعية",
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A-%D9%88%D8%A7%D9%84%D9%87%D9%86%D8%AF%D8%B3%D8%A9-%D8%A7%D9%84%D8%A7%D8%AC%D8%AA%D9%85%D8%A7%D8%B9%D9%8A%D8%A9/p1788324593",
    tools: [
      "Kali Linux",
      "OSINT",
      "Phishing Simulation",
      "Social Engineering",
      "Maltego",
      "Recon-ng",
    ],
    outcomes: [
      "تحليل سيناريو هجوم هندسة اجتماعية حقيقي",
      "بناء تقرير تهديدات احترافي",
      "تطبيق OSINT لجمع المعلومات بشكل أخلاقي",
    ],
  },
  {
    num: 2,
    title: "أساسيات أمن تطبيقات الويب",
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A3%D9%85%D9%86-%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA-%D8%A7%D9%84%D9%88%D9%8A%D8%A8/p543134201",
    tools: ["Burp Suite", "DVWA", "OWASP ZAP", "SQLMap", "XSS", "CSRF", "WAF"],
    outcomes: [
      "اكتشاف ثغرات OWASP Top 10 يدوياً",
      "كتابة تقرير ثغرات احترافي",
      "تطبيق WAF وإعدادات الحماية على تطبيق حقيقي",
    ],
  },
  {
    num: 3,
    title: "أساسيات أمن الشبكات وتحليل الحزم",
    href: "https://store.afaaqwetaan.com/%D8%A3%D8%B3%D8%A7%D8%B3%D9%8A%D8%A7%D8%AA-%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B4%D8%A8%D9%83%D8%A7%D8%AA-%D9%88%D8%AA%D8%AD%D9%84%D9%8A%D9%84-%D8%A7%D9%84%D8%AD%D8%B2%D9%85/p2027148318",
    tools: [
      "Wireshark",
      "Nmap",
      "Snort",
      "pfSense",
      "IDS/IPS",
      "VPN",
      "Firewall Rules",
    ],
    outcomes: [
      "تحليل حزم شبكية واكتشاف نشاط مشبوه",
      "إعداد IDS/IPS لبيئة شبكية محاكاة",
      "بناء خطة حماية شبكة مؤسسية",
    ],
  },
  {
    num: 4,
    title: "DevSecOps وأمن الأكواد والتطبيقات",
    href: "https://store.afaaqwetaan.com/devsecops-%D9%88%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%A3%D9%83%D9%88%D8%A7%D8%AF-%D9%88%D8%A7%D9%84%D8%AA%D8%B7%D8%A8%D9%8A%D9%82%D8%A7%D8%AA/p1117768535",
    tools: [
      "GitHub Actions",
      "SAST",
      "SonarQube",
      "Secrets Management",
      "Docker Security",
      "CI/CD Security",
    ],
    outcomes: [
      "دمج فحص الأمان في pipeline التطوير",
      "اكتشاف ثغرات في الكود قبل النشر",
      "تأمين بيئة Docker والـ containers",
    ],
  },
  {
    num: 5,
    title: "اختبار الاختراق والتقارير الأمنية",
    href: "https://store.afaaqwetaan.com/%D8%A7%D8%AE%D8%AA%D8%A8%D8%A7%D8%B1-%D8%A7%D9%84%D8%A7%D8%AE%D8%AA%D8%B1%D8%A7%D9%82-%D9%88%D8%A7%D9%84%D8%AA%D9%82%D8%A7%D8%B1%D9%8A%D8%B1-%D8%A7%D9%84%D8%A3%D9%85%D9%86%D9%8A%D8%A9/p1576467493",
    tools: [
      "Metasploit",
      "CVSS",
      "Pentest Methodology",
      "Executive Report",
      "Technical Report",
      "Incident Response",
    ],
    outcomes: [
      "تنفيذ pentest كامل على بيئة محاكاة",
      "كتابة تقرير أمني تنفيذي وتقني",
      "بناء خطة استجابة للحوادث الأمنية",
    ],
  },
];

const FOR_WHO = [
  {
    icon: "💻",
    title: "خريجو تقنية المعلومات",
    desc: "شبكات، برمجة، نظم معلومات — تريد تدخل مجال الأمن السيبراني بأدوات حقيقية وليس فقط شهادات.",
  },
  {
    icon: "🛡️",
    title: "العامل في أمن المعلومات",
    desc: "تشتغل في IT أو SOC وتريد تطوير مهارات pentest وDevSecOps وكتابة التقارير الأمنية الاحترافية.",
  },
  {
    icon: "🎯",
    title: "المهتم بأمن التطبيقات",
    desc: "مطور يريد فهم كيف يُهاجَم كوده، أو شخص جاد يريد دخول مجال bug bounty واختبار الاختراق.",
  },
];

const PROJECTS = [
  {
    code: "WTQ",
    name: "وثيق",
    desc: "نظام توثيق رقمي يعزز الثقة والشفافية في التعاملات بأدوات أمنية متقدمة.",
    track: "Cybersecurity",
  },
  {
    code: "CHK",
    name: "CyberHook",
    desc: "أداة اختبار اختراق تكشف ثغرات تطبيقات الويب وفق OWASP وتولّد تقارير أمنية.",
    track: "Cybersecurity",
  },
  {
    code: "NWS",
    name: "NWSA",
    desc: "نظام متكامل لرصد أمن الشبكات وتطبيقات الويب في الوقت الفعلي مع تنبيهات ذكية.",
    track: "Cybersecurity",
  },
];

const FAQS = [
  {
    q: "هل أحتاج خلفية برمجية للانضمام؟",
    a: "تحتاج معرفة أساسية بالشبكات ونظم التشغيل (Windows / Linux). البرمجة مفيدة لكنها ليست شرطاً — ستتعلم ما تحتاجه أثناء المسار. إذا كنت قادراً على قراءة سطر أوامر، فأنت مؤهل.",
  },
  {
    q: "ما الفرق بين هذا المسار وشهادة CompTIA Security+؟",
    a: "Security+ نظرية تجيب عليها بأسئلة خيارات. هذا المسار تدريبي تطبيقي — تشغّل Burp Suite، تنفّذ Pentest حقيقي، وتكتب تقرير أمني. الشهادتان مكمّلتان لبعضهما.",
  },
  {
    q: "هل يمكنني العمل في bug bounty بعد المسار؟",
    a: "نعم — المسار يُعطيك أدوات واسعة الاستخدام في bug bounty خصوصاً محطة أمن الويب ومحطة اختبار الاختراق. لكن bug bounty يحتاج خبرة مستمرة ومحاولات متعددة بعد التخرج.",
  },
  {
    q: "كيف يختلف هذا المسار عن الدورات العادية؟",
    a: "الدورات العادية تعطيك مفاهيم. آفاق تضعك في بيئة محاكاة حقيقية، تتعلم بالتطبيق الفعلي، وتخرج بمشروع Pentest كامل مع تقرير أمني قابل للإضافة لملفك المهني.",
  },
  {
    q: "ما ساعات التدريب؟",
    a: "الجلسات الحية: ٩ صباحاً – ٣ عصراً (الأحد–الخميس). لقاءات إثرائية أسبوعية مع خبراء الأمن: ٧–٩ مساءً. التسجيلات متاحة بعد كل جلسة. تحتاج ٦ إلى ٨ ساعات يومياً.",
  },
  {
    q: "هل الدفع بالتقسيط متاح؟",
    a: "نعم — التقسيط عبر <strong>تمارا</strong> متاح مباشرة من صفحة المنتج في متجر آفاق. لا حاجة لأي إجراء إضافي — الخيار يظهر تلقائياً عند الدفع.",
  },
];

/* ══ MAIN COMPONENT ══════════════════════════ */
export default function CyberTrackPage() {
  const [openMod, setOpenMod] = useState(0);

  return (
    <div
      className="min-h-screen bg-[#081828] text-[#F7F2E9] font-['IBM_Plex_Sans_Arabic',sans-serif]"
      dir="rtl"
    >
      {/* ══ HERO ══════════════════════════════════════ */}
      <section className="relative overflow-hidden pt-[var(--nav-h,64px)]">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 50% at 50% -10%,rgba(26,144,112,0.12) 0%,transparent 65%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 40% at 0% 60%,rgba(14,42,71,0.8) 0%,transparent 70%)",
            }}
          />
          {/* circuit grid */}
          <svg
            className="absolute inset-0 w-full h-full opacity-[.04]"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <pattern
                id="grid-cy"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="#1A9070"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-cy)" />
          </svg>
        </div>

        <div className="relative z-10 px-[clamp(16px,5vw,80px)] py-[clamp(48px,8vh,96px)]">
          {/* breadcrumb */}
          <div className="flex items-center gap-2 mb-[clamp(24px,3vh,40px)]">
            <a
              href="/"
              className="font-mono text-[.58rem] tracking-[.12em] uppercase text-[#3D6278] hover:text-[#94B4C8] transition-colors duration-200"
            >
              الرئيسية
            </a>
            <span className="text-[#3D6278] text-[.5rem]">◆</span>
            <span className="font-mono text-[.58rem] tracking-[.12em] uppercase text-[#22B58A]">
              مسار الأمن السيبراني
            </span>
          </div>

          <div className="grid gap-[clamp(32px,5vw,64px)] lg:grid-cols-[1fr_auto]">
            <div className="max-w-[640px]">
              <div className="inline-flex items-center gap-2 mb-[clamp(16px,2vh,24px)]">
                <span
                  className="w-[8px] h-[8px] rounded-full animate-pulse"
                  style={{ background: SHIELD2 }}
                />
                <span className="font-mono text-[.62rem] tracking-[.18em] uppercase text-[#22B58A] font-bold">
                  CYBERSECURITY · مسار مكثّف
                </span>
              </div>

              <h1
                className="font-cairo font-black leading-[1.05] tracking-[-0.03em] text-[#F7F2E9] mb-[clamp(12px,1.5vh,20px)]"
                style={{ fontSize: "clamp(2.2rem,5vw,4.2rem)" }}
              >
                مسار
                <br />
                <em className="not-italic" style={{ color: SHIELD2 }}>
                  الأمن السيبراني
                </em>
                <br />
                في التطبيقات
              </h1>

              <p className="text-[clamp(.9rem,.95rem,1.05rem)] text-[#94B4C8] leading-[1.9] mb-[clamp(20px,3vh,36px)] max-w-[500px]">
                ٥ محطات متسلسلة تأخذك من أساسيات الهجوم والدفاع إلى Pentest كامل
                مع تقرير أمني احترافي تُقدّمه أمام لجنة تقييم.
              </p>

              <div className="flex flex-wrap gap-2 mb-[clamp(24px,3.5vh,40px)]">
                {[
                  "Kali Linux",
                  "Burp Suite",
                  "Wireshark",
                  "Metasploit",
                  "DevSecOps",
                  "OWASP Top 10",
                ].map((t) => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A/c887145818"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 font-cairo font-bold px-[28px] py-[13px] rounded-[4px] transition-all duration-200 hover:opacity-90 hover:-translate-y-[1px]"
                  style={{
                    background: SHIELD,
                    color: "#fff",
                    fontSize: "clamp(.88rem,.95rem,1rem)",
                  }}
                >
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  سجّل في المسار
                </a>
                <a
                  href="#curriculum"
                  className="inline-flex items-center gap-2 font-cairo font-medium px-[24px] py-[13px] rounded-[4px] border border-[rgba(247,242,233,.15)] text-[#94B4C8] transition-all duration-200 hover:border-[rgba(247,242,233,.3)] hover:text-[#F7F2E9]"
                  style={{ fontSize: "clamp(.86rem,.92rem,.96rem)" }}
                >
                  استعرض المحتوى
                </a>
              </div>
            </div>

            {/* stats card */}
            <div className="rounded-[16px] border border-[rgba(26,144,112,.2)] bg-[#0C1F35] p-[clamp(20px,2.5vw,32px)] self-start min-w-[220px]">
              <div
                className="h-[3px] rounded-full mb-[20px]"
                style={{
                  background: `linear-gradient(90deg,${SHIELD},${SHIELD2})`,
                }}
              />
              <div className="grid grid-cols-2 gap-x-[16px] gap-y-[20px]">
                <StatBox n="١٠" label="أسابيع مكثفة" />
                <StatBox n="٣٠٠+" label="ساعة تدريب" />
                <StatBox n="٥" label="محطات متسلسلة" />
                <StatBox n="9.25" label="تقييم المتدربين" />
              </div>
              <div className="mt-[20px] pt-[16px] border-t border-[rgba(109,211,167,.07)]">
                <div className="flex items-center gap-2 mb-[6px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#6DD3A7] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    تدريب أونلاين مباشر + تسجيلات
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-[6px]">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#6DD3A7] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    شهادة إتمام معتمدة
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-[6px] h-[6px] rounded-full bg-[#F2B949] flex-shrink-0" />
                  <span className="text-[.72rem] text-[#94B4C8]">
                    دفع بالتقسيط عبر تمارا
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="h-[60px]"
          style={{
            background: "linear-gradient(to bottom, transparent, #081828)",
          }}
        />
      </section>

      {/* ══ PILLARS ═══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)]">
        <div className="text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            ما يميّز آفاق
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            رحلة متكاملة — مو مجرد محتوى
          </h2>
        </div>

        <div className="grid gap-[clamp(12px,1.5vw,20px)] md:grid-cols-3">
          {[
            {
              icon: "🔐",
              title: "التطوير التقني",
              color: SHIELD2,
              items: [
                "اختبار الاختراق والثغرات",
                "أمن تطبيقات الويب",
                "أمن الشبكات وتحليل الحزم",
                "DevSecOps وأمن الأكواد",
              ],
            },
            {
              icon: "📋",
              title: "التطوير المهني",
              color: MINT,
              items: [
                "+١٠ لقاءات مع خبراء الأمن",
                "كتابة التقارير الأمنية الاحترافية",
                "بناء البورتفوليو والـ LinkedIn",
                "فهم سوق الأمن السيبراني السعودي",
              ],
            },
            {
              icon: "🌟",
              title: "التطوير الشخصي",
              color: GOLD,
              items: [
                "اختبار Gallup مواطن القوة",
                "التفكير النقدي وحل المشكلات",
                "مهارات التواصل وعرض التقارير",
                "بناء الثقة وإثبات الكفاءة",
              ],
            },
          ].map((p) => (
            <div
              key={p.title}
              className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(20px,2vw,28px)] overflow-hidden relative"
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: p.color }}
              />
              <span className="text-[2rem] block mb-[12px]">{p.icon}</span>
              <h3
                className="font-cairo font-bold text-[1.05rem] mb-[14px]"
                style={{ color: p.color }}
              >
                {p.title}
              </h3>
              <ul className="space-y-[8px]">
                {p.items.map((i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[.78rem] text-[#94B4C8] leading-[1.6]"
                  >
                    <span
                      className="mt-[4px] flex-shrink-0 text-[.45rem]"
                      style={{ color: p.color }}
                    >
                      ◆
                    </span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CURRICULUM ════════════════════════════════ */}
      <section
        id="curriculum"
        className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)]"
      >
        <div className="flex flex-col items-center text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            CURRICULUM · المنهج
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight mb-[8px]">
            ٥ محطات — من الهجوم إلى الدفاع
          </h2>
          <p className="text-[.88rem] text-[#94B4C8] max-w-[480px] leading-[1.8]">
            كل محطة تبني على السابقة — تنتهي بمشروع صغير يتراكم ليصبح Pentest
            Report متكامل في المحطة الخامسة.
          </p>
        </div>

        <div className="space-y-[8px] max-w-[860px] mx-auto">
          {MODULES.map((m, i) => (
            <Module
              key={m.num}
              {...m}
              open={openMod === i}
              onToggle={() => setOpenMod(openMod === i ? -1 : i)}
            />
          ))}
        </div>

        {/* bundle CTA */}
        <div className="mt-[clamp(28px,4vh,44px)] max-w-[860px] mx-auto rounded-[14px] border border-[rgba(26,144,112,.2)] bg-[#0C1F35] p-[clamp(20px,2.5vw,32px)] flex flex-col md:flex-row items-start md:items-center gap-[20px] justify-between relative overflow-hidden">
          <div
            className="absolute top-0 left-0 right-0 h-[2px]"
            style={{
              background: `linear-gradient(90deg,${SHIELD},${SHIELD2})`,
            }}
          />
          <div>
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px]">
              المسار الكامل
            </p>
            <p className="font-cairo font-black text-[clamp(1rem,1.5vw,1.3rem)] text-[#F7F2E9] mb-[6px]">
              سجّل في المسار كاملاً
            </p>
            <p className="text-[.78rem] text-[#94B4C8]">
              ٥ محطات · ٣٠٠+ ساعة · Pentest Report · شهادة معتمدة
            </p>
          </div>
          <div className="flex items-center gap-[24px] flex-shrink-0">
            <div className="text-center">
              <span className="block font-mono text-[.48rem] text-[#3D6278] tracking-[.1em] uppercase mb-[2px]">
                كل المحطات
              </span>
              <span className="font-cairo font-black text-[1.6rem] text-[#F2B949]">
                ٦٧٥٠ ر
              </span>
            </div>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A/c887145818"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 font-cairo font-bold px-[22px] py-[11px] rounded-[4px] transition-all duration-200 hover:opacity-90 whitespace-nowrap"
              style={{ background: SHIELD, color: "#fff", fontSize: ".88rem" }}
            >
              سجّل الآن
            </a>
          </div>
        </div>
      </section>

      {/* ══ OUTCOMES ══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="grid gap-[clamp(32px,5vw,64px)] lg:grid-cols-2 items-start">
          <div>
            <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[10px]">
              بعد الرحلة
            </p>
            <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight mb-[16px]">
              من ستكون بعد إتمام المسار
            </h2>
            <p className="text-[.88rem] text-[#94B4C8] leading-[1.85] mb-[28px]">
              ما تخرج بشهادة حضور — تخرج بـ
              <strong className="text-[#F7F2E9]">
                {" "}
                Pentest Report احترافي
              </strong>{" "}
              وملف قابل للإضافة مباشرة لـ LinkedIn وسيرتك الذاتية.
            </p>
            <ul className="space-y-[10px]">
              {[
                "اكتشاف ثغرات OWASP Top 10 يدوياً باستخدام Burp Suite",
                "تحليل حزم شبكية واكتشاف نشاط مشبوه بـ Wireshark",
                "دمج فحص الأمان في pipeline التطوير (DevSecOps)",
                "تنفيذ Pentest كامل على بيئة محاكاة حقيقية",
                "كتابة تقرير أمني تنفيذي وتقني احترافي",
                "بناء خطة استجابة للحوادث الأمنية",
              ].map((o) => (
                <Check key={o}>{o}</Check>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#3D6278] mb-[14px]">
              المسمّيات الوظيفية التي تتأهل لها
            </p>
            <div className="grid grid-cols-2 gap-[10px]">
              {[
                { title: "Penetration Tester", sub: "مختبر اختراق" },
                { title: "Security Analyst", sub: "محلل أمن معلومات" },
                { title: "SOC Analyst", sub: "محلل عمليات أمنية" },
                { title: "AppSec Engineer", sub: "مهندس أمن تطبيقات" },
                { title: "DevSecOps Engineer", sub: "مهندس DevSecOps" },
                { title: "Security Consultant", sub: "مستشار أمن معلومات" },
              ].map((j) => (
                <div
                  key={j.title}
                  className="rounded-[10px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[14px] hover:border-[rgba(26,144,112,.2)] transition-colors duration-200"
                >
                  <span className="block font-mono text-[.7rem] font-bold text-[#22B58A] mb-[2px]">
                    {j.title}
                  </span>
                  <span className="block text-[.72rem] text-[#3D6278]">
                    {j.sub}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHO IS IT FOR ════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(32px,4vh,48px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            لمن هذا المسار؟
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            آفاق مو للكل — هي لك إذا…
          </h2>
        </div>
        <div className="grid gap-[clamp(12px,1.5vw,18px)] md:grid-cols-3 max-w-[860px] mx-auto">
          {FOR_WHO.map((f) => (
            <div
              key={f.title}
              className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(18px,2vw,26px)]"
            >
              <span className="text-[2.2rem] block mb-[10px]">{f.icon}</span>
              <h3 className="font-cairo font-bold text-[.98rem] text-[#F7F2E9] mb-[8px]">
                {f.title}
              </h3>
              <p className="text-[.78rem] text-[#94B4C8] leading-[1.7]">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-[20px] max-w-[860px] mx-auto rounded-[10px] border border-[rgba(247,242,233,.07)] bg-[#0C1F35] p-[clamp(14px,1.8vw,20px)] flex items-start gap-3">
          <span className="text-[1.1rem] flex-shrink-0 mt-[2px]">⚠️</span>
          <p className="text-[.78rem] text-[#3D6278] leading-[1.7]">
            <strong className="text-[#94B4C8]">آفاق مو للكل:</strong> هذا المسار
            يتطلب التزاماً يومياً جاداً. إذا كنت تبحث عن محتوى نظري تشوفه بوقتك،
            دوراتنا المسجّلة أنسب لك.
          </p>
        </div>
      </section>

      {/* ══ PROJECTS ══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(28px,3.5vh,44px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            مشاريع الخريجين
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.5rem,2.5vw,2.2rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            مشاريع حطّت بأمان
          </h2>
        </div>
        <div className="grid gap-[clamp(10px,1.2vw,14px)] sm:grid-cols-3 max-w-[860px] mx-auto">
          {PROJECTS.map((p) => (
            <div
              key={p.code}
              className="rounded-[12px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[18px] hover:border-[rgba(26,144,112,.2)] transition-colors duration-200 overflow-hidden relative"
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: SHIELD }}
              />
              <span className="font-mono text-[.56rem] tracking-[.1em] text-[#3D6278] block mb-[6px]">
                AFQ → {p.code}
              </span>
              <h4 className="font-cairo font-black text-[1rem] text-[#22B58A] mb-[6px]">
                {p.name}
              </h4>
              <p className="text-[.74rem] text-[#94B4C8] leading-[1.65]">
                {p.desc}
              </p>
              <div className="mt-[10px] inline-flex items-center gap-1.5 text-[.6rem] font-mono text-[#6DD3A7]">
                <span>✓</span>
                <span>LANDED SAFELY</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ PRICING ═══════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(32px,4vh,52px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            السعر والتسجيل
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.6rem,3vw,2.6rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            احجز تذكرتك
          </h2>
        </div>

        <div className="grid gap-[clamp(12px,1.5vw,18px)] md:grid-cols-2 max-w-[720px] mx-auto">
          {/* single */}
          <div className="rounded-[14px] border border-[rgba(109,211,167,.09)] bg-[#0C1F35] p-[clamp(20px,2.5vw,30px)] flex flex-col">
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px]">
              محطة واحدة
            </p>
            <p className="font-cairo font-black text-[1.05rem] text-[#F7F2E9] mb-[12px]">
              أي محطة تختارها
            </p>
            <div className="flex items-baseline gap-2 mb-[16px]">
              <span className="font-cairo font-black text-[2.4rem] text-[#F2B949] leading-none">
                ١٣٥٠
              </span>
              <span className="text-[#94B4C8] text-[.82rem]">
                ر · محطة واحدة
              </span>
            </div>
            <ul className="space-y-[6px] mb-[20px] flex-1">
              {[
                "٦٠ ساعة تدريبية",
                "تسجيلات الجلسات",
                "مشروع المحطة",
                "شهادة إتمام المحطة",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-[.76rem] text-[#94B4C8]"
                >
                  <span className="text-[#22B58A] text-[.45rem]">◆</span>
                  {i}
                </li>
              ))}
            </ul>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A/c887145818"
              target="_blank"
              rel="noopener"
              className="flex items-center justify-center gap-2 font-cairo font-bold py-[11px] rounded-[4px] border border-[rgba(26,144,112,.3)] text-[#22B58A] transition-all duration-200 hover:bg-[rgba(26,144,112,.1)] text-[.88rem]"
            >
              اختر المحطة
            </a>
          </div>

          {/* full — featured */}
          <div className="rounded-[14px] border border-[rgba(26,144,112,.3)] bg-[#0C1F35] p-[clamp(20px,2.5vw,30px)] flex flex-col relative overflow-hidden">
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{
                background: `linear-gradient(90deg,${SHIELD},${SHIELD2})`,
              }}
            />
            <div className="absolute top-[16px] left-[16px]">
              <span className="font-mono text-[.46rem] tracking-[.1em] uppercase bg-[rgba(26,144,112,.12)] border border-[rgba(26,144,112,.25)] text-[#22B58A] px-[8px] py-[3px] rounded-full">
                الأفضل قيمة
              </span>
            </div>
            <p className="font-mono text-[.52rem] tracking-[.14em] uppercase text-[#3D6278] mb-[4px] mt-[8px]">
              المسار الكامل
            </p>
            <p className="font-cairo font-black text-[1.05rem] text-[#F7F2E9] mb-[12px]">
              ٥ محطات متسلسلة
            </p>
            <div className="flex items-baseline gap-2 mb-[4px]">
              <span className="font-cairo font-black text-[2.4rem] text-[#F2B949] leading-none">
                ٦٧٥٠
              </span>
              <span className="text-[#94B4C8] text-[.82rem]">ر</span>
            </div>
            <p className="text-[.68rem] text-[#3D6278] mb-[16px]">
              أو بالتقسيط عبر تمارا
            </p>
            <ul className="space-y-[6px] mb-[20px] flex-1">
              {[
                "٣٠٠+ ساعة تدريبية",
                "لقاءات أسبوعية مع خبراء الأمن",
                "Pentest Report كامل",
                "شهادة إتمام المسار المعتمدة",
                "مجتمع خريجي آفاق",
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 text-[.76rem] text-[#94B4C8]"
                >
                  <span className="text-[#22B58A] text-[.45rem]">◆</span>
                  {i}
                </li>
              ))}
            </ul>
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A/c887145818"
              target="_blank"
              rel="noopener"
              className="flex items-center justify-center gap-2 font-cairo font-bold py-[12px] rounded-[4px] transition-all duration-200 hover:opacity-90 text-[.9rem]"
              style={{ background: SHIELD, color: "#fff" }}
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              سجّل في المسار
            </a>
          </div>
        </div>

        <p className="text-center mt-[16px] text-[.72rem] text-[#3D6278] font-mono tracking-[.06em]">
          ◆ الدفع بالتقسيط متاح عبر تمارا — يظهر الخيار تلقائياً عند الدفع في
          سلة
        </p>
      </section>

      {/* ══ FAQ ═══════════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(48px,7vh,80px)] border-t border-[rgba(109,211,167,.07)]">
        <div className="text-center mb-[clamp(28px,3.5vh,44px)]">
          <p className="font-mono text-[.6rem] tracking-[.2em] uppercase text-[#22B58A] mb-[8px]">
            الأسئلة الشائعة
          </p>
          <h2 className="font-cairo font-black text-[clamp(1.5rem,2.5vw,2.2rem)] text-[#F7F2E9] leading-[1.1] tracking-tight">
            كل سؤال في ذهنك
          </h2>
        </div>
        <div className="max-w-[720px] mx-auto space-y-[8px]">
          {FAQS.map((f) => (
            <FAQItem key={f.q} {...f} />
          ))}
        </div>
      </section>

      {/* ══ FINAL CTA ═════════════════════════════════ */}
      <section className="px-[clamp(16px,5vw,80px)] py-[clamp(56px,8vh,96px)] border-t border-[rgba(109,211,167,.08)] relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 100%,rgba(26,144,112,0.08) 0%,transparent 65%)",
          }}
        />
        <div className="relative z-10 text-center max-w-[580px] mx-auto">
          <span className="inline-flex items-center gap-2 mb-[20px]">
            <span
              className="w-[6px] h-[6px] rounded-full animate-pulse"
              style={{ background: SHIELD2 }}
            />
            <span className="font-mono text-[.6rem] tracking-[.18em] uppercase text-[#22B58A]">
              BOARDING NOW · التسجيل مفتوح
            </span>
            <span
              className="w-[6px] h-[6px] rounded-full animate-pulse"
              style={{ background: SHIELD2 }}
            />
          </span>
          <h2 className="font-cairo font-black text-[clamp(1.8rem,4vw,3.2rem)] text-[#F7F2E9] leading-[1.08] tracking-tight mb-[14px]">
            جاهز تبدأ
            <br />
            <em className="not-italic" style={{ color: SHIELD2 }}>
              رحلتك؟
            </em>
          </h2>
          <p className="text-[.9rem] text-[#94B4C8] leading-[1.85] mb-[32px]">
            المقاعد محدودة لضمان جودة التجربة. سجّل الآن وابدأ رحلتك نحو سوق
            الأمن السيبراني.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://store.afaaqwetaan.com/%D8%A7%D9%84%D8%A3%D9%85%D9%86-%D8%A7%D9%84%D8%B3%D9%8A%D8%A8%D8%B1%D8%A7%D9%86%D9%8A/c887145818"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 font-cairo font-bold px-[32px] py-[14px] rounded-[4px] text-white transition-all duration-200 hover:opacity-90 hover:-translate-y-[1px]"
              style={{
                background: SHIELD,
                fontSize: "clamp(.9rem,.98rem,1.05rem)",
              }}
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              سجّل في مسار الأمن السيبراني
            </a>
            <a
              href="mailto:afaaq@wetaan.com"
              className="inline-flex items-center gap-2 font-cairo font-medium px-[24px] py-[14px] rounded-[4px] border border-[rgba(247,242,233,.15)] text-[#94B4C8] transition-all duration-200 hover:text-[#F7F2E9] hover:border-[rgba(247,242,233,.3)]"
              style={{ fontSize: "clamp(.86rem,.92rem,.96rem)" }}
            >
              تواصل معنا
            </a>
          </div>
          <div className="flex items-center justify-center gap-[clamp(12px,2vw,24px)] mt-[24px] flex-wrap">
            {[
              "دفع بالتقسيط عبر تمارا",
              "تدريب أونلاين",
              "شهادة معتمدة",
              "مجتمع مستمر بعد التخرج",
            ].map((t) => (
              <span
                key={t}
                className="flex items-center gap-1.5 font-mono text-[.56rem] text-[#3D6278] tracking-[.06em]"
              >
                <span className="text-[.42rem]" style={{ color: MINT }}>
                  ◆
                </span>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
